import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { checkLivestock, type CoralType, type LivestockCategory } from '../../services/maturityService';

/**
 * Maturity notices while adding an animal (#1825): soft warnings / hints with
 * the reason, confidence and source. They never block saving. Coral asks for
 * its type (only to check the SPS rule; it is not saved).
 */
export function LivestockMaturityNotice({ aquariumId, category }: { aquariumId: string; category: LivestockCategory | '' }) {
  const { t } = useTranslation();
  const [coralType, setCoralType] = useState<CoralType>('unknown');
  const relevant = category === 'coral' || category === 'fish';
  const query = useQuery({
    queryKey: ['maturity', aquariumId, 'livestock-check', category, category === 'coral' ? coralType : ''],
    queryFn: () => checkLivestock(aquariumId, category as LivestockCategory, category === 'coral' ? coralType : undefined),
    enabled: relevant && Boolean(aquariumId),
    retry: false,
  });
  if (!relevant) return null;
  return (
    <Stack gap="3" data-testid="livestock-maturity">
      {category === 'coral' && (
        <Stack gap="1">
          <Select
            id="livestock-coral-type"
            label={t('newTank.gates.coralType')}
            value={coralType}
            options={(['unknown', 'soft', 'lps', 'sps'] as const).map((v) => ({ value: v, label: String(t(`newTank.gates.coralTypes.${v}`)) }))}
            onChange={(e) => setCoralType(e.target.value as CoralType)}
            aria-describedby="livestock-coral-type-help"
          />
          <Text color="muted" variant="caption" id="livestock-coral-type-help">{t('newTank.gates.coralTypeHelp')}</Text>
        </Stack>
      )}
      {(query.data ?? []).map((n) => (
        <Alert
          key={n.gate}
          tone={n.severity === 'warning' ? 'warning' : 'info'}
          live="polite"
          title={t(`newTank.gates.${n.gate}.title`)}
          data-testid={`gate-${n.gate}`}
        >
          <Stack gap="1">
            <Text>
              {String(
                t(`newTank.gates.${n.gate}.${n.variant}`, {
                  ...n.values,
                  parameter: n.values.parameter ? String(t(`measurement.history.parameterNames.${String(n.values.parameter)}`)) : '',
                }),
              )}
            </Text>
            <Text color="muted" variant="caption">
              {t('newTank.gates.sourceLine', { source: n.source, confidence: t(`reefGuard.confidence.${n.confidence}`) })}
            </Text>
            <Text color="muted" variant="caption">{t('newTank.gates.canProceed')}</Text>
          </Stack>
        </Alert>
      ))}
    </Stack>
  );
}
